import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { UserMessage } from '../../DesignSystem/LiliUserMessage/UserMessage';
import { LiliPasswordInput } from '../../DesignSystem/LiliPasswordInput/LiliPasswordInput';
import { CloseButton } from '../../DesignSystem/LiliCloseButton/CloseButton';
import { VIEWS_MAP } from '../../router/constants';
import { useChangePasswordMutation } from '../../hooks/mutations/useChangePasswordMutation';
import { useStore } from '../../store';
import { FORGOT_PASSWORD_FLOW } from '../login/pages/LoginHelp/constants';

export interface ChangePasswordProps {
  onClose: () => void;
  onPasswordChanged: () => void;
}

export const ChangePassword = ({ onClose, onPasswordChanged }: ChangePasswordProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.currUser());
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [wrongPasswordErrorMessage, setWrongPasswordErrorMessage] = useState<string | null>(null);

  const { isLoading, mutate: mutateChangePassword } = useChangePasswordMutation(
    { customerId },
    onPasswordChanged,
    (e) => {
      if (e.code === 40035) {
        setWrongPasswordErrorMessage(t('CHANGE_PASSWORD.ERRORS.INCORRECT_PASSWORD'));
      } else {
        setErrorMessage(e.message);
      }
    },
  );

  const onSubmit = () => {
    setErrorMessage(undefined);
    setWrongPasswordErrorMessage(null);
    mutateChangePassword({ existingPassword: password, newPassword });
  };
  const onNewPasswordChange = (input: string) => {
    setErrorMessage(undefined);
    setWrongPasswordErrorMessage(null);
    setNewPassword(input);
  };
  const onPasswordChange = (input: string) => {
    setErrorMessage(undefined);
    setWrongPasswordErrorMessage(null);
    setPassword(input);
  };
  return (
    <UserMessage id='change-password-modal' onClick={() => {}} shouldBlur>
      <div className='flex flex-col  rounded-[12px]  animate-fadeIn px-[24px] pt-[16px] pb-[32px] w-[504px] h-[490px] bg-bw00'>
        <div className='w-full flex flex-row justify-end'>
          <CloseButton id='close-button' onClick={onClose} />
        </div>
        <p className='text-bw08 text-[24px] font-metricRMedium w-full text-center mb-[24px]'>
          {t('CHANGE_PASSWORD.TITLE')}
        </p>

        <p className='text-bw08 text-[16px] font-metricR mb-[8px]'>{t('CHANGE_PASSWORD.CURRENT_PASSWORD_TITLE')}</p>
        <LiliPasswordInput
          onChange={(p) => onPasswordChange(p)}
          id='current-password-input'
          title={t('CHANGE_PASSWORD.CURRENT_PASSWORD_PLACEHOLDER')}
          errorMessage={wrongPasswordErrorMessage || undefined}
          inilineChild={
            <button
              className='text-bw06 hover:text-bw07 font-metricR text-[14px] min-w-[150px] py-[0px] h-fit'
              id='forgot-password-button'
              onClick={() => navigate(`/${VIEWS_MAP.RECOVERY}/password`)}
              type='button'
            >
              {t('CHANGE_PASSWORD.FORGOT_PASSWORD_BUTTON')}
            </button>
          }
        />
        <p className='text-bw08 text-[16px] font-metricR mb-[8px] mt-[32px]'>
          {t('CHANGE_PASSWORD.NEW_PASSWORD_TITLE')}
        </p>
        <LiliPasswordInput
          id='password-input'
          title={t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.INPUT_TITLE)}
          onChange={onNewPasswordChange}
          errorMessage={errorMessage}
          passwordStrengthHints={[
            t('CHANGE_PASSWORD.PASSWORD_HINTS.RULE_1'),
            t('CHANGE_PASSWORD.PASSWORD_HINTS.RULE_2'),
            t('CHANGE_PASSWORD.PASSWORD_HINTS.RULE_3'),
          ]}
          passwordStrengthTexts={{
            tooWeak: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_TOO_WEAK),
            weak: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_WEAK),
            medium: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_FAIR),
            strong: t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.PASSWORD_STRENGTH_STRONG),
          }}
        />
        <div className='h-[64px]' />
        <LiliButton
          id='submit-button'
          title={t('CHANGE_PASSWORD.SUBMIT_BUTTON')}
          onClick={onSubmit}
          isLoading={isLoading}
          disabled={password.length < 8 || newPassword.length < 8}
        />
      </div>
    </UserMessage>
  );
};
